import React, { lazy, Suspense } from 'react'

import { Navigate } from 'react-router-dom'

import Spinner from '@/atoms/spinner/spinner'
import { isDevelopment } from '@/helpers/config'
// Public pages
import PublicLayout from '@/layout/public-layout'
import { UserRole } from '@/models/auth'
import { clientPath, getDefaultUrl, path } from '@/navigation'
import LoginPage from '@/pages/public-pages/login'

const VerifyInvoicePage = React.lazy(() => import('@/pages/public-pages/verify-invoice'))
const VerifyOutstandingAmountPage = React.lazy(() => import('@/pages/public-pages/verify-outstanding-amount'))
const ResetPasswordPage = lazy(() => import('@/pages/public-pages/reset-password'))
const ChangePasswordPage = React.lazy(() => import('@/pages/public-pages/change-password'))
const PaymentReminderPage = React.lazy(() => import('@/pages/public-pages/payment-reminder'))

// Back office pages
const BackOfficePrivateLayout = React.lazy(() => import('@/layout/back-office-private-layout'))
// Risk management
// Prospects
const ProspectsListPage = lazy(() => import('@/pages/back-office-pages/risk-management/prospects-list'))
const ProspectDetailsPage = lazy(() => import('@/pages/back-office-pages/risk-management/prospect-details'))
const ProspectDebtorDetailsPage = lazy(() => import('@/pages/back-office-pages/risk-management/prospect-debtor-details'))
const KycDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/prospect-kyc-details'))
//Reports
const ReportsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/reports'))
//Stoplist
const StopListPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/stoplist'))
const StopListCompanyPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/stoplist-company-details'))
const StopListPersonPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/stoplist-person-details'))
//Annual review
const AnnualReviewPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/annual-review'))
const AnnualReviewDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/annual-review-details'))
const AnnualReviewDebtorDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/annual-review-debtor-details'))
const AnnualReviewKycDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/annual-review-kyc-details'))
const AnnualReviewContractDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/annual-review-contract-details'))
// Limit increase
const LimitIncreasePage = React.lazy(() => import('@/pages/back-office-pages/risk-management/limit-increase'))
const LimitIncreaseDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/limit-increase-details'))
const LimitIncreaseHistoryDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/limit-increase-history-details'))
const LimitIncreaseDebtorDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/limit-increase-debtor-details'))
const LimitIncreaseDebtorHistoryDetailsPage = React.lazy(
  () => import('@/pages/back-office-pages/risk-management/limit-increase-debtor-history-details'),
)
//Contracts
const ProspectContractDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/contract-details'))
// Monitoring tasks
const MonitoringTasksPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/monitoring-tasks'))
const MonitoringTaskDetailsPage = React.lazy(() => import('@/pages/back-office-pages/risk-management/monitoring-task-details'))

// System preferences
// System
const SystemPage = React.lazy(() => import('@/pages/back-office-pages/system-preferences/system'))
const UserDetailsPage = React.lazy(() => import('@/pages/back-office-pages/system-preferences/user-details'))
const DebtorGroupDetailsPage = React.lazy(() => import('@/pages/back-office-pages/system-preferences/debtor-group-details'))
const BrokerDetailsPage = React.lazy(() => import('@/pages/back-office-pages/system-preferences/broker-details'))
// Reporting
const ReportingPage = React.lazy(() => import('@/pages/back-office-pages/system-preferences/reporting'))
// Clients
// Clients
const ClientsListPage = React.lazy(() => import('@/pages/back-office-pages/clients/clients-list'))
const ClientDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/clients-details'))
const ClientContractDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/factoring-contract-details'))
const ClientDebtorDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/client-debtor-details'))
const ClientSettlementDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/client-settlement-details'))
const ClientCreditNoteDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/client-credit-note-details'))
const LoanBookContractDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/loan-book-contract-details'))
// Debtors
const DebtorsPage = React.lazy(() => import('@/pages/back-office-pages/clients/debtors-list'))
const DebtorsDetailsPage = React.lazy(() => import('@/pages/back-office-pages/clients/debtor-details'))
// Factoring
// Invoices
const InvoicesPage = React.lazy(() => import('@/pages/back-office-pages/factoring/invoices-list'))
const InvoiceDetailsPage = React.lazy(() => import('@/pages/back-office-pages/factoring/invoice-details'))
// Notes
const NotesListPage = React.lazy(() => import('@/pages/back-office-pages/factoring/notes-list'))
// Debt collection
const DebtCollectionPage = React.lazy(() => import('@/pages/back-office-pages/factoring/debt-collection-list'))
const DebtCollectionDetailsPage = React.lazy(() => import('@/pages/back-office-pages/factoring/debt-collection-details'))
// Client requirements
const ClientRequirementsPage = React.lazy(() => import('@/pages/back-office-pages/factoring/client-requirements'))
// Operations tasks
const OutstandingVerificationTasksPage = React.lazy(() => import('@/pages/back-office-pages/factoring/outstanding-verification-tasks'))
const OutstandingVerificationTaskDetailsPage = React.lazy(
  () => import('@/pages/back-office-pages/factoring/outstanding-verification-task-details'),
)
const BulkExternalVerificationTasksPage = React.lazy(() => import('@/pages/back-office-pages/factoring/bulk-external-verification-tasks'))
const BulkExternalVerificationTaskDetailsPage = React.lazy(
  () => import('@/pages/back-office-pages/factoring/bulk-external-verification-task-details'),
)
const AiTasksPage = React.lazy(() => import('@/pages/back-office-pages/factoring/ai-tasks'))
const AiTaskDetailsPage = React.lazy(() => import('@/pages/back-office-pages/factoring/ai-task-details'))
const AiTaskDocumentDetailsPage = React.lazy(() => import('@/pages/back-office-pages/factoring/ai-task-document-details'))
// Payments
// Bank statements
const BankStatementsListPage = React.lazy(() => import('@/pages/back-office-pages/payments/bank-statements-list'))
const BankStatementDetailsPage = React.lazy(() => import('@/pages/back-office-pages/payments/bank-statement-details'))
const BankTransactionDetailsPage = React.lazy(() => import('@/pages/back-office-pages/payments/bank-transaction-details'))
// General ledgers
const GeneralLedgerPage = React.lazy(() => import('@/pages/back-office-pages/payments/general-ledgers'))
const GeneralLedgerReconciliationsPage = React.lazy(() => import('@/pages/back-office-pages/payments/general-ledger-reconciliations'))
const GeneralLedgerReconciliationInvestigationDetailsPage = React.lazy(
  () => import('@/pages/back-office-pages/payments/investigation-details'),
)
// Reports
const PaymentsReportsPage = React.lazy(() => import('@/pages/back-office-pages/payments/payments-reports'))
// Payouts
const PayoutsListPage = React.lazy(() => import('@/pages/back-office-pages/payments/payouts-list'))
const FactoringPayoutDetailsPage = React.lazy(() => import('@/pages/back-office-pages/payments/factoring-payout-details'))
const LoanBookPayoutDetailsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/payout-details'))
const InvestmentPayoutDetailsPage = React.lazy(() => import('@/pages/back-office-pages/payments/investments-payout-details'))

// Loan book
// Loans
const LoansPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/loans-list'))
const LoanDetailsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/loan-details'))
// Credit lines
const CreditLinesPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/credit-line-list'))
const CreditLineDetailsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/credit-line-details'))
// Reverse factoring
const ReverseFactoringPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/reverse-factoring-list'))
const ReversefactoringDetailsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/reverse-factoring-details'))
// Notes
const LoanBookNotesPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/notes-list'))
// Reports
const LoanBookReportsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/loan-book-reports'))
// Debt collection
const LoanBookDebtCollectionPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/debt-collection-list'))
const LoanBookDebtCollectionDetailsPage = React.lazy(() => import('@/pages/back-office-pages/loan-book/debt-collection-details'))

// Investment
// Offers
const OffersPage = React.lazy(() => import('@/pages/back-office-pages/investment/offers-list'))
const OfferDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/offer-details'))
// Investors
const InvestorsPage = React.lazy(() => import('@/pages/back-office-pages/investment/investors-list'))
const InvestorDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/investor-details'))
const InvestorKycIdentityDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/investor-kyc-identity-details'))
// Investment plans
const InvestorInvestmentPlanDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/investment-plan-details'))
const InvestmentPlansPage = React.lazy(() => import('@/pages/back-office-pages/investment/investment-plans'))
// Notes
const InvestmentNotesPage = React.lazy(() => import('@/pages/back-office-pages/investment/notes-list'))
// Task manager
const InvestmentTaskManagerPage = React.lazy(() => import('@/pages/back-office-pages/investment/task-manager'))
const InvestmentTaskDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/task-details'))
const ReinvestmentDetailsPage = React.lazy(() => import('@/pages/back-office-pages/investment/reinvestment-details'))

// Client pages
const ClientPortalPrivateLayout = React.lazy(() => import('@/layout/client-portal-private-layout'))
// Home
const ClientPortalHomePage = React.lazy(() => import('@/pages/client-pages/home-page'))
const ClientPortalContractDetailsPage = React.lazy(() => import('@/pages/client-pages/client-contract-details'))
const ClientPortalLoanBookContractDetailsPage = React.lazy(() => import('@/pages/client-pages/loan-book-contract-details'))
// Debtors
const ClientPortalDebtorsPage = React.lazy(() => import('@/pages/client-pages/debtors-list'))
const ClientPortalDebtorDetailsPage = React.lazy(() => import('@/pages/client-pages/debtor-details'))
const ClientPortalClientCreditNoteDetailsPage = React.lazy(() => import('@/pages/client-pages/client-credit-note-details'))
// Invoices
const ClientPortalInvoicesPage = React.lazy(() => import('@/pages/client-pages/invoices-list'))
const ClientPortalInvoiceDetailsPage = React.lazy(() => import('@/pages/client-pages/invoice-details'))
// Notes
const ClientPortalNotesPage = React.lazy(() => import('@/pages/client-pages/notes-list/notes-list-page'))
// Users
const ClientPortalUsersPage = React.lazy(() => import('@/pages/client-pages/users-list'))
// Settlements
const ClientPortalSettlementsPage = React.lazy(() => import('@/pages/client-pages/settlements-list'))
const ClientPortalSettlementDetailsPage = React.lazy(() => import('@/pages/client-pages/settlement-details'))
// Reports
const ClientPortalReportsPage = React.lazy(() => import('@/pages/client-pages/reports'))
// Loan book
const ClientPortalLoanBookPayoutsPage = React.lazy(() => import('@/pages/client-pages/loan-book-payouts-list'))
const ClientPortalLoanBookPayoutDetailsPage = React.lazy(() => import('@/pages/client-pages/loan-book-payout-details'))

const LazyLoader = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<Spinner overlay />}>{children}</Suspense>

const publicRoutes = [
  {
    element: <PublicLayout />,
    children: [
      {
        path: path.loginPage,
        element: <LoginPage />,
      },
      {
        path: path.changePassword,
        element: (
          <LazyLoader>
            <ChangePasswordPage />
          </LazyLoader>
        ),
      },
      {
        path: path.resetPassword,
        element: (
          <LazyLoader>
            <ResetPasswordPage />
          </LazyLoader>
        ),
      },
      {
        path: `:lang${path.verifyInvoice}`,
        element: (
          <LazyLoader>
            <VerifyInvoicePage />
          </LazyLoader>
        ),
      },
      {
        path: `:lang${path.verifyOutstandingAmount}`,
        element: (
          <LazyLoader>
            <VerifyOutstandingAmountPage />
          </LazyLoader>
        ),
      },
      {
        path: `:lang${path.paymentReminder}`,
        element: (
          <LazyLoader>
            <PaymentReminderPage />
          </LazyLoader>
        ),
      },
    ],
  },
  {
    path: '/*',
    element: <Navigate replace to={path.loginPage} />,
  },
]

const clientRoutes = [
  {
    element: (
      <LazyLoader>
        <ClientPortalPrivateLayout />
      </LazyLoader>
    ),
    children: [
      {
        path: clientPath.home,
        element: (
          <LazyLoader>
            <ClientPortalHomePage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.contractDetails,
        element: (
          <LazyLoader>
            <ClientPortalContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.loanBookContractDetails,
        element: (
          <LazyLoader>
            <ClientPortalLoanBookContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.debtors,
        element: (
          <LazyLoader>
            <ClientPortalDebtorsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.debtorDetails,
        element: (
          <LazyLoader>
            <ClientPortalDebtorDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.clientCreditNoteDetails,
        element: (
          <LazyLoader>
            <ClientPortalClientCreditNoteDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.invoices,
        element: (
          <LazyLoader>
            <ClientPortalInvoicesPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.loanBookPayouts,
        element: (
          <LazyLoader>
            <ClientPortalLoanBookPayoutsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.loanBookPayoutDetails,
        element: (
          <LazyLoader>
            <ClientPortalLoanBookPayoutDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.invoiceDetails,
        element: (
          <LazyLoader>
            <ClientPortalInvoiceDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.notes,
        element: (
          <LazyLoader>
            <ClientPortalNotesPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.users,
        element: (
          <LazyLoader>
            <ClientPortalUsersPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.settlements,
        element: (
          <LazyLoader>
            <ClientPortalSettlementsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.settlementDetails,
        element: (
          <LazyLoader>
            <ClientPortalSettlementDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: clientPath.reports,
        element: (
          <LazyLoader>
            <ClientPortalReportsPage />
          </LazyLoader>
        ),
      },
      {
        path: '/*',
        element: <Navigate replace to={clientPath.debtors} />,
      },
    ],
  },
]

const backOfficeRoutes = [
  {
    element: (
      <LazyLoader>
        <BackOfficePrivateLayout />
      </LazyLoader>
    ),
    children: [
      {
        path: path.prospects,
        element: (
          <LazyLoader>
            <ProspectsListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.prospectDetails,
        element: (
          <LazyLoader>
            <ProspectDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.prospectDebtorDetails,
        element: (
          <LazyLoader>
            <ProspectDebtorDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.kycDetails,
        element: (
          <LazyLoader>
            <KycDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.reports,
        element: (
          <LazyLoader>
            <ReportsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.stopList,
        element: (
          <LazyLoader>
            <StopListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.stopListCompanyDetails,
        element: (
          <LazyLoader>
            <StopListCompanyPage />
          </LazyLoader>
        ),
      },
      {
        path: path.stopListPersonDetails,
        element: (
          <LazyLoader>
            <StopListPersonPage />
          </LazyLoader>
        ),
      },
      {
        path: path.annualReview,
        element: (
          <LazyLoader>
            <AnnualReviewPage />
          </LazyLoader>
        ),
      },
      {
        path: path.annualReviewDetails,
        element: (
          <LazyLoader>
            <AnnualReviewDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.annualReviewDebtorDetails,
        element: (
          <LazyLoader>
            <AnnualReviewDebtorDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.annualReviewKycDetails,
        element: (
          <LazyLoader>
            <AnnualReviewKycDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.annualReviewContractDetails,
        element: (
          <LazyLoader>
            <AnnualReviewContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.limitIncrease,
        element: (
          <LazyLoader>
            <LimitIncreasePage />
          </LazyLoader>
        ),
      },
      {
        path: path.limitIncreaseDetails,
        element: (
          <LazyLoader>
            <LimitIncreaseDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.limitIncreaseHistoryDetails,
        element: (
          <LazyLoader>
            <LimitIncreaseHistoryDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.limitIncreaseDebtorDetails,
        element: (
          <LazyLoader>
            <LimitIncreaseDebtorDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.limitIncreaseDebtorHistoryDetails,
        element: (
          <LazyLoader>
            <LimitIncreaseDebtorHistoryDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.prospectContractDetails,
        element: (
          <LazyLoader>
            <ProspectContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.riskMonitoringTasks,
        element: (
          <LazyLoader>
            <MonitoringTasksPage />
          </LazyLoader>
        ),
      },
      {
        path: path.riskMonitoringTaskDetails,
        element: (
          <LazyLoader>
            <MonitoringTaskDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.system,
        element: (
          <LazyLoader>
            <SystemPage />
          </LazyLoader>
        ),
      },
      {
        path: path.userDetails,
        element: (
          <LazyLoader>
            <UserDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: '/*',
        element: <Navigate replace to={getDefaultUrl(UserRole.BACK_OFFICE_USER)} />,
      },
      {
        path: path.debtorGroupDetails,
        element: (
          <LazyLoader>
            <DebtorGroupDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.brokerDetails,
        element: (
          <LazyLoader>
            <BrokerDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clients,
        element: (
          <LazyLoader>
            <ClientsListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clientsDetails,
        element: (
          <LazyLoader>
            <ClientDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.factoringContractDetails,
        element: (
          <LazyLoader>
            <ClientContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookContractDetails,
        element: (
          <LazyLoader>
            <LoanBookContractDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clientDebtorDetails,
        element: (
          <LazyLoader>
            <ClientDebtorDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clientSettlementDetails,
        element: (
          <LazyLoader>
            <ClientSettlementDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clientCreditNoteDetails,
        element: (
          <LazyLoader>
            <ClientCreditNoteDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.debtors,
        element: (
          <LazyLoader>
            <DebtorsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.debtorDetails,
        element: (
          <LazyLoader>
            <DebtorsDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.invoices,
        element: (
          <LazyLoader>
            <InvoicesPage />
          </LazyLoader>
        ),
      },
      {
        path: path.invoiceDetails,
        element: (
          <LazyLoader>
            <InvoiceDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.debtCollection,
        element: (
          <LazyLoader>
            <DebtCollectionPage />
          </LazyLoader>
        ),
      },
      {
        path: path.debtCollectionDetails,
        element: (
          <LazyLoader>
            <DebtCollectionDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.reporting,
        element: (
          <LazyLoader>
            <ReportingPage />
          </LazyLoader>
        ),
      },
      {
        path: path.bankStatements,
        element: (
          <LazyLoader>
            <BankStatementsListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.bankStatementDetails,
        element: (
          <LazyLoader>
            <BankStatementDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.bankTransactionDetails,
        element: (
          <LazyLoader>
            <BankTransactionDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.generalLedgers,
        element: (
          <LazyLoader>
            <GeneralLedgerPage />
          </LazyLoader>
        ),
      },
      {
        path: path.paymentsReports,
        element: (
          <LazyLoader>
            <PaymentsReportsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.generalLedgerReconciliations,
        element: (
          <LazyLoader>
            <GeneralLedgerReconciliationsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.generalLedgerReconciliationInvestigationDetails,
        element: (
          <LazyLoader>
            <GeneralLedgerReconciliationInvestigationDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.payouts,
        element: (
          <LazyLoader>
            <PayoutsListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.notes,
        element: (
          <LazyLoader>
            <NotesListPage />
          </LazyLoader>
        ),
      },
      {
        path: path.clientRequirements,
        element: (
          <LazyLoader>
            <ClientRequirementsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.factoringPayoutDetails,
        element: (
          <LazyLoader>
            <FactoringPayoutDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.investmentPayoutDetails,
        element: (
          <LazyLoader>
            <InvestmentPayoutDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.outstandingVerificationTasks,
        element: (
          <LazyLoader>
            <OutstandingVerificationTasksPage />
          </LazyLoader>
        ),
      },
      {
        path: path.outstandingVerificationTaskDetails,
        element: (
          <LazyLoader>
            <OutstandingVerificationTaskDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.bulkVerificationTasks,
        element: (
          <LazyLoader>
            <BulkExternalVerificationTasksPage />
          </LazyLoader>
        ),
      },
      {
        path: path.bulkVerificationTaskDetails,
        element: (
          <LazyLoader>
            <BulkExternalVerificationTaskDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.aiTasks,
        element: (
          <LazyLoader>
            <AiTasksPage />
          </LazyLoader>
        ),
      },
      {
        path: path.aiTaskDetails,
        element: (
          <LazyLoader>
            <AiTaskDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.aiTaskDocumentDetails,
        element: (
          <LazyLoader>
            <AiTaskDocumentDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loans,
        element: (
          <LazyLoader>
            <LoansPage />
          </LazyLoader>
        ),
      },
      {
        path: path.creditLines,
        element: (
          <LazyLoader>
            <CreditLinesPage />
          </LazyLoader>
        ),
      },
      {
        path: path.reverseFactoring,
        element: (
          <LazyLoader>
            <ReverseFactoringPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookNotes,
        element: (
          <LazyLoader>
            <LoanBookNotesPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanDetails,
        element: (
          <LazyLoader>
            <LoanDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.creditLineDetails,
        element: (
          <LazyLoader>
            <CreditLineDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.reverseFactoringDetails,
        element: (
          <LazyLoader>
            <ReversefactoringDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookPayoutDetails,
        element: (
          <LazyLoader>
            <LoanBookPayoutDetailsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookReports,
        element: (
          <LazyLoader>
            <LoanBookReportsPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookDebtCollection,
        element: (
          <LazyLoader>
            <LoanBookDebtCollectionPage />
          </LazyLoader>
        ),
      },
      {
        path: path.loanBookDebtCollectionDetails,
        element: (
          <LazyLoader>
            <LoanBookDebtCollectionDetailsPage />
          </LazyLoader>
        ),
      },
      ...(isDevelopment
        ? [
            {
              path: path.investors,
              element: (
                <LazyLoader>
                  <InvestorsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investorDetails,
              element: (
                <LazyLoader>
                  <InvestorDetailsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investorKYCDetails,
              element: (
                <LazyLoader>
                  <InvestorKycIdentityDetailsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investmentPlanDetails,
              element: (
                <LazyLoader>
                  <InvestorInvestmentPlanDetailsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.offers,
              element: (
                <LazyLoader>
                  <OffersPage />
                </LazyLoader>
              ),
            },
            {
              path: path.offerDetails,
              element: (
                <LazyLoader>
                  <OfferDetailsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investmentPlans,
              element: (
                <LazyLoader>
                  <InvestmentPlansPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investmentNotes,
              element: (
                <LazyLoader>
                  <InvestmentNotesPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investmentTaskManager,
              element: (
                <LazyLoader>
                  <InvestmentTaskManagerPage />
                </LazyLoader>
              ),
            },
            {
              path: path.investmentTaskDetails,
              element: (
                <LazyLoader>
                  <InvestmentTaskDetailsPage />
                </LazyLoader>
              ),
            },
            {
              path: path.reinvestmentDetails,
              element: (
                <LazyLoader>
                  <ReinvestmentDetailsPage />
                </LazyLoader>
              ),
            },
          ]
        : []),
    ],
  },
]

const ROLES_ROUTES = {
  [UserRole.CLIENT]: clientRoutes,
  [UserRole.BACK_OFFICE_USER]: backOfficeRoutes,
}

export const getRoutes = (role: UserRole) => [...(ROLES_ROUTES[role] ?? []), ...publicRoutes]
